import type { Quantity, WindZoneGeometry, ZoneResult } from '../../gen/ejot/roofing/v1/types';
import { formatQuantityWithUnit } from '../../shared/format';

export type ZonePressureKind = 'design' | 'characteristic' | 'unavailable';

export interface ZonePressurePresentation {
  kind: ZonePressureKind;
  quantity?: Quantity;
  label: string;
  valueText: string;
}

const SUBSCRIPT: Record<string, string> = {
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
  '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
};

/** Presentation-only conversion of F(12) to F₍₁₂₎. */
export function formatZoneDisplayLabel(value: string | undefined): string {
  if (!value) return '';
  const match = /^([FGHI])\((\d+)\)$/.exec(value);
  if (!match) return value;
  const ordinal = [...match[2]!].map((digit) => SUBSCRIPT[digit] ?? digit).join('');
  return `${match[1]}₍${ordinal}₎`;
}

export function displayZoneFact(result: ZoneResult | undefined): ZonePressurePresentation {
  if (result?.design_pressure?.value !== undefined) {
    return {
      kind: 'design',
      quantity: result.design_pressure,
      label: 'Design pressure',
      valueText: formatQuantityWithUnit(result.design_pressure, 'Unavailable'),
    };
  }
  if (result?.characteristic_pressure?.value !== undefined) {
    return {
      kind: 'characteristic',
      quantity: result.characteristic_pressure,
      label: 'Characteristic pressure',
      valueText: formatQuantityWithUnit(result.characteristic_pressure, 'Unavailable'),
    };
  }
  return {
    kind: 'unavailable',
    label: 'Pressure',
    valueText: 'Unavailable',
  };
}

/** Stable two-line label content shared by 2D and 3D renderers. */
export function zoneEngineeringLabel(
  zone: WindZoneGeometry,
  result: ZoneResult | undefined,
): { zoneText: string; pressureText?: string; pressureKind: ZonePressureKind } {
  const displayed = displayZoneFact(result);
  const rawLabel = zone.display_label || result?.display_label || zone.id || '—';
  return {
    zoneText: formatZoneDisplayLabel(rawLabel),
    pressureText: displayed.kind === 'unavailable' ? undefined : displayed.valueText,
    pressureKind: displayed.kind,
  };
}
